import type { TFilterKeys } from 'watchtower-ui/components/Filter/constants';
import type { TDay } from 'watchtower-ui/types/date';

/**
 * Country ISO type
 * @example 'BR', 'CO', 'KR', 'AR', 'CN', 'ZA', 'MX'
 */
export type CountryISO = string;

/**
 * Default country ISO code as fallback
 */
export const DEFAULT_COUNTRY: CountryISO = 'BR';

/**
 * Global Watchtower pseudo-country code. Routed at `/global`; reuses the
 * country app shell but bypasses validCountries auth checks (see Country.tsx).
 */
export const GLOBAL_COUNTRY_CODE: CountryISO = 'GLOBAL';

/**
 * Pseudo-country codes — values that travel through `selectedCountry` and the
 * `/:countryCode` URL slot but are NOT real markets. Use to short-circuit
 * per-country logic (membership, currency, API base URL, etc.).
 */
export const PSEUDO_COUNTRIES: CountryISO[] = [GLOBAL_COUNTRY_CODE];

export const isPseudoCountry = (code: string | undefined | null): boolean =>
  !!code && PSEUDO_COUNTRIES.includes(code.toUpperCase());

/**
 * Country code exceptions mapping
 * Maps non-standard country codes to their standard ISO equivalents
 */
export const COUNTRY_CODE_EXCEPTION_MAP: Record<string, CountryISO> = {};

/**
 * Countries marked as "Coming Soon" via VITE_COMING_SOON_COUNTRIES env variable.
 * Set in .env.prod as comma-separated codes (e.g. "AR,KR").
 * Empty in non-prod environments so all countries work normally.
 */
export const COMING_SOON_COUNTRIES: CountryISO[] = ((import.meta.env.VITE_COMING_SOON_COUNTRIES as string) ?? '')
  .split(',')
  .map((c) => c.trim().toUpperCase())
  .filter(Boolean);

/**
 * Available countries for the application.
 * These are configured statically; add new countries here as they become supported.
 */
export const AVAILABLE_COUNTRIES: CountryISO[] = ['BR', 'CO', 'KR', 'AR', 'CN', 'ZA', 'MX'];

/**
 * Possible country based config keys will be added here
 */
type TCountryConfig = 'weekStartDay' | 'additionalLevels';

type TCountryCustomConfig = {
  weekStartDay?: TDay | null;
  excludeLevels?: TFilterKeys[] | null;
  additionalLevels?: TFilterKeys[] | null;
};

type TCountryCustomConfigKeys = keyof TCountryCustomConfig;

type TCountryNonCustomKeys = Exclude<TCountryConfig, TCountryCustomConfigKeys>;

/**
 * Unless otherwise specified, country config values will default to string.
 */
export type TCountryConfigMap = {
  [key in TCountryNonCustomKeys]: string | null;
} & TCountryCustomConfig;

export const DEFAULT_COUNTRY_CONFIG_MAP: TCountryConfigMap = {};

/**
 * Country-specific configuration mapping
 * TODO: Fetch this list from an API in the future
 */
export const COUNTRY_CONFIG_MAP: Partial<Record<CountryISO, Partial<TCountryConfigMap>>> = {
  KR: {
    excludeLevels: ['region'],
  },
  ZA: {
    additionalLevels: ['alcohol_category'],
  },
  CN: {
    additionalLevels: ['bu'],
  },
};

export const getCountryConfig = (countryISO: CountryISO): TCountryConfigMap => ({
  ...DEFAULT_COUNTRY_CONFIG_MAP,
  ...COUNTRY_CONFIG_MAP[countryISO],
});

export const COUNTRY_CONFIG_ISO_COUNTRIES: CountryISO[] = Object.keys(COUNTRY_CONFIG_MAP);
